// Captures the browser's "install this app" event. `beforeinstallprompt` fires once, early (often before
// any React page has mounted), so this module is imported from src/index.tsx and listens from then on;
// components read the result through subscribeInstallPrompt / getInstallPromptState.

export type InstallChoice = { outcome: "accepted" | "dismissed"; platform?: string };

export type BeforeInstallPromptEvent = Event & {
    prompt: () => Promise<void>;
    userChoice: Promise<InstallChoice>;
};

export type InstallPromptState = {
    /** A deferred prompt is waiting and prompt() can be called. */
    canPrompt: boolean;
    /** The app is running installed, or `appinstalled` fired in this session. */
    installed: boolean;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installedThisSession = false;
let state: InstallPromptState = { canPrompt: false, installed: false };
const listeners = new Set<() => void>();

type NavigatorWithStandalone = Navigator & { standalone?: boolean };

export function isRunningStandalone(): boolean {
    if (typeof window === "undefined") return false;
    try {
        if (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) {
            return true;
        }
    } catch {
        // matchMedia unavailable or rejected the query: fall through.
    }
    return (navigator as NavigatorWithStandalone).standalone === true;
}

/** iPhone, iPod, iPad (including iPadOS, which reports itself as a Mac with a touch screen). */
export function isIosDevice(): boolean {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

function recompute(): void {
    const next: InstallPromptState = {
        canPrompt: deferredPrompt !== null,
        installed: installedThisSession || isRunningStandalone(),
    };
    if (next.canPrompt === state.canPrompt && next.installed === state.installed) return;
    state = next;
    listeners.forEach((listener) => listener());
}

export function getInstallPromptState(): InstallPromptState {
    // Re-read standalone each time so a store created before the media query settled is still right.
    const installed = installedThisSession || isRunningStandalone();
    if (installed !== state.installed) state = { ...state, installed };
    return state;
}

export function subscribeInstallPrompt(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Shows the browser's install dialog. The deferred event is single-use, so it is cleared whichever way
 * the learner answers. Resolves to null when there is nothing to show.
 */
export async function promptInstall(): Promise<InstallChoice["outcome"] | null> {
    const event = deferredPrompt;
    if (!event) return null;
    deferredPrompt = null;
    recompute();
    try {
        await event.prompt();
        const choice = await event.userChoice;
        return choice.outcome;
    } catch {
        return null;
    }
}

function onBeforeInstallPrompt(event: Event): void {
    // Stops the browser's own mini-infobar so the profile card is the one place that offers it.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    recompute();
}

function onAppInstalled(): void {
    installedThisSession = true;
    deferredPrompt = null;
    recompute();
}

export function initInstallPrompt(): void {
    if (typeof window === "undefined" || typeof window.addEventListener !== "function") return;
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    recompute();
}

/** Test helper: forgets everything and detaches the window listeners. */
export function resetInstallPromptForTests(): void {
    window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.removeEventListener("appinstalled", onAppInstalled);
    deferredPrompt = null;
    installedThisSession = false;
    state = { canPrompt: false, installed: false };
    listeners.clear();
}
